import { PartialWith } from "api/common/utils/type.utils";
import {
  useCreateEcrDocMutation,
  useDeleteEcrDocMutation,
} from "api/memberPortal/ecr/docs/ecrDocs.api";
import { EcrDoc } from "api/memberPortal/ecr/docs/ecrDocs.types";
import {
  useEcrCreateMutation,
  useEcrUpdateMutation,
} from "api/memberPortal/ecr/ecr.api";
import {
  Ecr,
  EcrDocPayload,
  EcrPayload,
  EcrUpdatePayload,
} from "api/memberPortal/ecr/ecr.types";
import { ECR_STATES } from "api/memberPortal/ecr/ecrOptions.types";
import {
  useCreateEcrTdMutation,
  useDeleteEcrTdMutation,
  useLazyGetEcrTdByEcrIdAndTdIdQuery,
} from "api/memberPortal/ecr/tds/ecrTds.api";
import {
  useRemoveFileMutation,
  useUploadFileMutation,
} from "api/memberPortal/files/files.api";
import { TechnicalDecision } from "api/memberPortal/technicalDecisions/technicalDecisions.types";
import { getISOCurrentDate } from "utils/timeUtils";

import { EcrFormSchema } from "./Ecr.schema";

type ParsedForm = ReturnType<typeof EcrFormSchema.parse>;

export const useEcrFormMutations = () => {
  const [triggerEcr] = useEcrCreateMutation();
  const [triggerEcrUpdate] = useEcrUpdateMutation();
  const [triggerFileUpload] = useUploadFileMutation();
  const [triggerRemoveFile] = useRemoveFileMutation();
  const [triggerEcrTd] = useCreateEcrTdMutation();
  const [triggerDeleteTd] = useDeleteEcrTdMutation();
  const [triggerDocType] = useCreateEcrDocMutation();
  const [triggerDeleteDoc] = useDeleteEcrDocMutation();
  const [triggerGetEcrTdByIds] = useLazyGetEcrTdByEcrIdAndTdIdQuery();

  const processFiles = (files: ParsedForm["files"], ecrid: number) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (file.file_id && file.active === false) {
        triggerRemoveFile(file.file_id);
      } else if (!file.file_id && file.file) {
        const fileData = new FormData();
        fileData.set("file", file.file);
        fileData.set("file_type_id", ecrid.toString());
        fileData.set("file_type", "ecr-comment");
        fileData.set("active", "true");
        fileData.set("file_name", file.file.name);
        fileData.set("file_label", file.fileLabel ?? "");
        fileData.set(
          "private",
          file.documentVisibility === "Public" ? "false" : "true",
        );
        fileData.set("uploaded_on", getISOCurrentDate());
        triggerFileUpload(fileData);
      }
    }
  };

  const createTechnicalDecisions = (
    technicalDecisions: ParsedForm["technicalDecisions"],
    ecrid: number,
    currentUserId: number,
  ) => {
    if (technicalDecisions.length === 0) return;
    technicalDecisions.forEach((td) => {
      triggerEcrTd({
        ecrid,
        td_id: td.value,
        creator: currentUserId,
        entrydate: getISOCurrentDate(),
      });
    });
  };

  const createDocTypes = (
    documentTypes: ParsedForm["documentTypes"],
    ecrid: number,
    currentUserId: number,
  ) => {
    if (!documentTypes || documentTypes.length === 0) return;
    documentTypes.forEach((docType) => {
      triggerDocType({
        ecrid,
        doc_type: docType,
        creator: currentUserId,
        entrydate: getISOCurrentDate(),
      } as EcrDocPayload);
    });
  };

  const diffAndUpdateDocTypes = (
    existingDocs: EcrDoc[] | undefined,
    newDocTypes: ParsedForm["documentTypes"],
    ecrid: number,
    currentUserId: number,
  ) => {
    const existingDocTypes = existingDocs?.map((d) => d.dtid.doctype) ?? [];
    const toCreate = newDocTypes.filter((dt) => !existingDocTypes.includes(dt));
    const toRemove =
      existingDocs?.filter((d) => !newDocTypes.includes(d.dtid.doctype)) ?? [];

    createDocTypes(toCreate, ecrid, currentUserId);
    toRemove.forEach((doc) => triggerDeleteDoc(doc.xid));
  };

  const diffAndUpdateTds = (
    existingTds: TechnicalDecision[] | undefined,
    newTds: ParsedForm["technicalDecisions"],
    ecrid: number,
    currentUserId: number,
  ) => {
    const existingTdIds = existingTds?.map((td) => td.td_id) ?? [];
    const toCreate = newTds.filter(
      (td) => !existingTdIds.includes(Number(td.value)),
    );
    const removedTdIds = existingTdIds.filter(
      (tdId) => !newTds.some((td) => Number(td.value) === tdId),
    );

    createTechnicalDecisions(toCreate, ecrid, currentUserId);
    removedTdIds.forEach(async (tdId) => {
      const result = await triggerGetEcrTdByIds({
        ecrid,
        tdid: tdId,
      }).unwrap();
      if (result?.length > 0) {
        triggerDeleteTd(result[0].xid);
      }
    });
  };

  const handleCreate = async (
    parsed: ParsedForm,
    currentUserId: number,
    maintenanceId?: number,
  ) => {
    const payload = {
      active: true,
      cite: parsed.cite,
      cmnt: parsed.comment,
      creator: currentUserId,
      entrydate: getISOCurrentDate(),
      gist: parsed.title,
      pid: parsed.product.value,
      pp_id: parsed.protectionProfile.value || null,
      process_location: maintenanceId
        ? "Assurance Maintenance"
        : parsed.process_location,
      rationale: parsed.rationale,
      req: parsed.requirement,
      state: ECR_STATES.PROPOSED,
      status: parsed.status,
      type: parsed.type,
      ...(maintenanceId && { maintenance_id: maintenanceId }),
    } as EcrPayload;

    const data = await triggerEcr(payload).unwrap();
    const { files, technicalDecisions, documentTypes } = parsed;
    const { ecrid } = data;

    // TODO: we should move these to the backend
    processFiles(files, ecrid);
    createTechnicalDecisions(technicalDecisions, ecrid, currentUserId);
    createDocTypes(documentTypes, ecrid, currentUserId);
  };

  const handleUpdate = async (
    parsed: ParsedForm,
    ecr: Ecr,
    existingDocs: EcrDoc[] | undefined,
    existingTds: TechnicalDecision[] | undefined,
    currentUserId: number,
  ) => {
    const payload = {
      ecrid: ecr.ecrid,
      cite: parsed.cite,
      cmnt: parsed.comment,
      editor: currentUserId,
      gist: parsed.title,
      pid: parsed.product.value,
      pp_id: parsed.protectionProfile.value || null,
      process_location: parsed.process_location,
      rationale: parsed.rationale,
      req: parsed.requirement,
      status: parsed.status,
      type: parsed.type,
      moddate: getISOCurrentDate(),
    } as PartialWith<EcrUpdatePayload, "ecrid">;

    const data = await triggerEcrUpdate(payload).unwrap();
    const { files, technicalDecisions, documentTypes } = parsed;
    const { ecrid } = data;

    // TODO: we should move these to the backend
    processFiles(files, ecrid);
    diffAndUpdateDocTypes(existingDocs, documentTypes, ecrid, currentUserId);
    diffAndUpdateTds(existingTds, technicalDecisions, ecrid, currentUserId);
  };

  return { handleCreate, handleUpdate };
};
